'use client';

const RECOVERY_ATTEMPTED_KEY = 'chunk-recovery-attempted';

/**
 * Returns `true` if the given error is a webpack/Next.js chunk-load failure.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  // Webpack chunks throw ChunkLoadError with "Loading chunk" in the message.
  // Dynamic import() rejections often produce "Failed to fetch" or 404 errors.
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk/i.test(error.message) ||
    (error.name === 'TypeError' && /failed to fetch/i.test(error.message)) ||
    /\.js(\?.*)? 404/i.test(error.message)
  );
}

/**
 * Clears all client-side caches and storage, then hard-reloads.
 */
export async function recoverFromStaleChunks(): Promise<void> {
  // Flush all Cache Storage API caches.
  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      // Some origins may not have caches — ignore.
    }
  }

  // Unregister all Service Workers so the next load is fully server-fresh.
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch {
      // Non-fatal.
    }
  }

  // Clear client-side cookies (HttpOnly cookies are untouched from JS).
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0]?.trim();
      if (!name) return;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch {
    // Non-fatal.
  }

  // Mark recovery as attempted — must be set *after* clearing sessionStorage
  // so it survives the wipe and prevents infinite loops on reload.
  try {
    localStorage.clear();
    sessionStorage.clear();
    sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, 'true');
  } catch {
    // Some environments (private browsing) may throw — non-fatal.
  }

  // Force a full server reload (bypass browser cache).
  window.location.reload();
}

/**
 * Returns `true` if we already attempted chunk recovery this session.
 * Used to break infinite reload loops.
 */
export function hasRecoveryBeenAttempted(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY) === 'true';
  } catch {
    return false;
  }
}
